<script>
    import { createEventDispatcher } from 'svelte'
    import {companies} from "../stores/companies.js";
    import {company_search_list} from "../stores/misc";
    import {I_COMPANY} from "../../../../../lib/datatypes.js";
    import {inArray} from "../../../../../lib/utils";
    import {PAGE_ID, navigateTo} from "../stores/router";

    const dispatch = createEventDispatcher();

    export let type = 'full';  // 'full' or 'box'
    export let selected_company_id = null;

    export let current_search_str = '';
    export let allow_adding_company = true;
    export let minimise_box_without_search = false;

    let company_buckets = {};
    let chosen_letter = '';
    const bucketByName = companies => {
        company_buckets = {};
        let company, letter;
        for (let i = 0, n = companies.length; i < n; i++) {
            company = companies[i];
            letter = company[I_COMPANY.NAME][0].toLowerCase();
            switch (letter) {
                case 'a':
                case 'b':
                case 'c':
                case 'd':
                case 'e':
                case 'f':
                case 'g':
                case 'h':
                case 'i':
                case 'j':
                case 'k':
                case 'l':
                case 'm':
                case 'n':
                case 'o':
                case 'p':
                case 'q':
                case 'r':
                case 's':
                case 't':
                case 'u':
                case 'v':
                case 'w':
                    if (company_buckets[letter] === undefined) company_buckets[letter] = [];
                    company_buckets[letter].push(company);
                    break;
                case 'x':
                case 'y':
                case 'z':
                    if (company_buckets['xyz'] === undefined) company_buckets['xyz'] = [];
                    company_buckets['xyz'].push(company);
                    break;
                default:
                    if (company_buckets['other'] === undefined) company_buckets['other'] = [];
                    company_buckets['other'].push(company);
            }
        }
    }

    $: searched_companies = current_search_str === '' ?
        $companies.list :
        $company_search_list.filter(entry => {
            return entry[0].includes(current_search_str.toLowerCase().replace(/\s+/g, ''));
        }).map(entry => {
            return $companies.lookup[entry[1]];
        });

    const MIN_NUMBER_IN_SEARCH_TO_COLLAPSE = 50;
    const MAX_NUMBER_OPTIONS_IN_TYPE_BOX = 20;
    $: {
        // Don't bucket by letter if box-type UI, or if relatively few companies in search
        if(type === 'box' || searched_companies.length < MIN_NUMBER_IN_SEARCH_TO_COLLAPSE) {
            company_buckets = {
                'all': searched_companies
            };
        }
        else {
            bucketByName(searched_companies);
        }
        if(!inArray(Object.keys(company_buckets), chosen_letter)) chosen_letter = Object.keys(company_buckets)[0];
    }

    const onCompanyClick = id => {
        dispatch('companyClicked', {id: id});
    };
    const onCancelClick = () => {
        current_search_str = '';
        dispatch('cancelClicked');
    };
</script>

{#if type === "full"}
    <div class="px-4 sm:px-6 lg:px-8">

        <form class="xmt-6 flex space-x-4" action="#">
            <div class="min-w-0 flex-1">
                <label for="search" class="sr-only">Search</label>
                <div class="relative rounded-md shadow-sm">
                    <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <svg class="h-5 w-5 text-gray-400" x-description="Heroicon name: mini/magnifying-glass" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"></path>
                        </svg>
                    </div>
                    <input bind:value={current_search_str} autofocus type="search" name="search" id="search" class="block w-full rounded-md border-gray-300 pl-10 focus:border-pink-500 focus:ring-pink-500 sm:text-sm" placeholder="Search">
                </div>
            </div>
            <button type="submit" class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2">
                <svg class="h-5 w-5 text-gray-400" x-description="Heroicon name: mini/funnel" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M2.628 1.601C5.028 1.206 7.49 1 10 1s4.973.206 7.372.601a.75.75 0 01.628.74v2.288a2.25 2.25 0 01-.659 1.59l-4.682 4.683a2.25 2.25 0 00-.659 1.59v3.037c0 .684-.31 1.33-.844 1.757l-1.937 1.55A.75.75 0 018 18.25v-5.757a2.25 2.25 0 00-.659-1.591L2.659 6.22A2.25 2.25 0 012 4.629V2.34a.75.75 0 01.628-.74z" clip-rule="evenodd"></path>
                </svg>
                <span class="sr-only">Search</span>
            </button>
        </form>

        <!-- Current: "z-10 bg-indigo-50 border-indigo-500 text-indigo-600", Default: "bg-white border-gray-300 text-gray-500 hover:bg-gray-50" -->
        <div class="mt-4 font-mono -space-x-px -space-y-px" aria-label="Pagination">{#each Object.keys(company_buckets) as letter}<span on:click={() => chosen_letter = letter} class="relative inline-flex items-center border -ml-px px-3 py-2 text-sm font-medium {letter === chosen_letter ? `z-10 bg-indigo-50 border-indigo-500 text-indigo-600` : `bg-white border-gray-300 text-gray-500 hover:bg-gray-50`} focus:z-20">{letter}</span>{/each}</div>

        <table class="min-w-full divide-y divide-gray-300">
            <thead>
            <tr>
                <th scope="col" class="py-3.5 pl-0 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Name</th>
    <!--            <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">Last Name</th>-->
    <!--            <th scope="col" class="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">Position</th>-->
    <!--            <th scope="col" class="relative py-3.5 pl-3 pr-0 text-left text-sm font-semibold text-gray-900 sm:pr-6">Company</th>-->
            </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
            {#each company_buckets[chosen_letter] as company (company[I_COMPANY.ID])}
                <tr data-id="{company[I_COMPANY.ID]}" on:click={() => onCompanyClick(company[I_COMPANY.ID])}
                    class="cursor-pointer">
                    <td class="w-full max-w-0 py-4 pl-0 pr-3 text-sm font-medium text-gray-900 align-top sm:w-auto sm:max-w-none sm:pl-6">
                        {company[I_COMPANY.NAME]}
                    </td>
    <!--                <td class="hidden px-3 py-4 text-sm font-medium text-gray-900 align-top lg:table-cell">{company[I_COMPANY.LAST_NAME]}</td>-->
    <!--                <td class="hidden px-3 py-4 text-sm text-gray-500 align-top sm:table-cell">{company[I_COMPANY.POSITION]}</td>-->
    <!--                <td class="py-4 pl-3 pr-6 text-sm text-gray-500 align-top sm:pr-0">{company[I_COMPANY.COMPANY_NAME]}</td>-->
                </tr>
            {/each}
            </tbody>
        </table>

    </div>
{/if}
{#if type === 'box'}
    <div>
        <div class="relative mt-1 rounded-md shadow-sm">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg class="h-5 w-5 text-gray-400" x-description="Heroicon name: mini/magnifying-glass" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd"></path>
                </svg>
            </div>
            <input bind:value={current_search_str} autofocus type="search" name="search" id="search" class="block w-full rounded-md border-gray-300 pl-10 focus:border-pink-500 focus:ring-pink-500 sm:text-sm" placeholder="Search">
        </div>
    </div>

    {#if (minimise_box_without_search && current_search_str !== '') || !minimise_box_without_search }

    <ol class="xabsolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base border border-gray-300 focus:outline-none sm:text-sm">
        {#each company_buckets[chosen_letter].slice(0, MAX_NUMBER_OPTIONS_IN_TYPE_BOX) as company (company[I_COMPANY.ID])}
        <li data-id="{company[I_COMPANY.ID]}" on:click={() => onCompanyClick(company[I_COMPANY.ID])}
            class="relative cursor-default select-none py-2 pl-8 pr-4 text-gray-900">
            {#if company[I_COMPANY.ID] === selected_company_id}
                <span class="block truncate font-semibold">{company[I_COMPANY.NAME]}</span>
                <span class="absolute inset-y-0 left-0 flex items-center pl-1.5 text-indigo-600">
                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" />
                    </svg>
                </span>
            {:else}
                <span class="block truncate">{company[I_COMPANY.NAME]}</span>
            {/if}
        </li>
        {/each}
    </ol>

    <div class="flex flex-shrink-0 justify-between mt-2">
        <button on:click={onCancelClick}
                type="button"
                class="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >Cancel</button>
        {#if allow_adding_company}
        <button on:click={() => navigateTo(PAGE_ID.ADD_COMPANY)}
                type="button" class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800">
            <!-- Heroicon name: mini/plus -->
            <svg class="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
            </svg>
            <span>or.. Add A New Company</span>
        </button>
        {/if}
    </div>

    {/if}
{/if}


<style></style>